import { useLayoutEffect, useRef, useState } from 'react'

import type { InvoiceData, InvoiceSetting } from '@/types/models'

/* 공식 거래명세서 재현 — 큰장(파랑·1장1건) / 작은장(빨강·1장2건).
   색·테두리·칸 구성·도장을 PDF 견본에 최대한 맞춤. */

const BLUE = '#1d4ed8'
const RED = '#e11d48'

/* 공급자 회사별 명세서 양식(invoice_style) → 열 구성 플래그. 기본 common(=그린푸드/공통).
   그린서울·마루팜·다원농산만 각자 변형. 실제 제출 양식 PDF와 대조해 매핑. */
function bigFlags(style?: string) {
  return {
    showCode: style === 'greenseoul' || style === 'marufarm',  // No.+코드 열(그린서울·마루팜만)
    showTodayTotal: style !== 'marufarm',                       // 당일거래총액(마루팜 제외)
  }
}
function smallFlags(style?: string) {
  const dawon = style === 'dawon'
  return {
    showSeq: dawon,       // 순번 열(다원)
    showCode: !dawon,     // 코드 열, 품명 앞(공통); 다원은 순번으로 대체
    splitAmount: dawon,   // 공급가액+세액 분리(다원) vs 단일 '금액'(공통)
    showItemMemo: dawon,  // 품목 비고 열(다원)
    footerSum: dawon,     // 하단 '합 계'(다원) vs 수량계/총계/부가세계/공급가액계(공통)
  }
}

/* 상호·성명·주소 등 가변 길이 텍스트를 한 줄로 유지하고, 셀 폭을 넘으면 폰트만
   축소(확대 안 함)해 맞춘다. 줄바꿈으로 헤더 높이가 커져 하단 라인이 잘리는 문제 방지. */
function FitText({ text, base, min = 7 }: { text: string; base: number; min?: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState(base)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    let s = base
    el.style.fontSize = `${s}px`
    if (el.clientWidth > 0) {
      let guard = 60
      while (s > min && el.scrollWidth > el.clientWidth && guard-- > 0) {
        s -= 0.5
        el.style.fontSize = `${s}px`
      }
    }
    setSize(s)
  }, [text, base, min])
  return (
    <div ref={ref} style={{ whiteSpace: 'nowrap', overflow: 'hidden', fontSize: size }}>{text}</div>
  )
}

function fmtInt(v: string | number | null | undefined): string {
  if (v == null || v === '') return ''
  const n = Number(v)
  return n ? n.toLocaleString() : ''
}
function fmtQty(v: string | number | null | undefined): string {
  if (v == null || v === '') return ''
  const n = Number(v)
  // 수량 0 은 공란 처리(금액 fmtInt 과 동일 규칙). 명세서에 "0.00" 이 찍히지 않게.
  return n ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''
}
function fmtDate(ymd: string): string {
  const [y, m, d] = (ymd || '').split('-')
  return y ? `${y} 년 ${m} 월 ${d} 일` : ''
}
/** 빈 행용 셀 n개 (key 포함). */
function blanks(n: number, accent: string, h: number) {
  return Array.from({ length: n }).map((_, j) => (
    <td key={j} style={{ border: `1px solid ${accent}`, height: h }}>&nbsp;</td>
  ))
}

/** 세로로 쌓은 라벨 (공 급 받 는 자). */
function VLabel({ text, accent }: { text: string; accent: string }) {
  return (
    <td
      style={{ border: `1px solid ${accent}`, color: accent, width: 20, textAlign: 'center', fontWeight: 700 }}
      rowSpan={4}
    >
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, fontSize: 11 }}>
        {text.split('').map((c, i) => <span key={i}>{c}</span>)}
      </div>
    </td>
  )
}

type Party = InvoiceData['partner']

/** 공급받는자 + 공급자 를 한 테두리 박스로. 공급자 우측에 도장 오버레이. */
function PartyTable({ accent, receiver, supplier }: { accent: string; receiver: Party; supplier: InvoiceData['company'] }) {
  const seal = supplier.seal || undefined
  const L = (t: string) => (
    <td style={{ border: `1px solid ${accent}`, color: accent, fontWeight: 700, whiteSpace: 'nowrap', padding: '1px 4px', textAlign: 'center', width: 68 }}>{t}</td>
  )
  const V = (t: React.ReactNode, span = 1) => (
    <td colSpan={span} style={{ border: `1px solid ${accent}`, padding: '1px 5px' }}>
      {typeof t === 'string' ? <FitText text={t} base={12} /> : t}
    </td>
  )
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed', position: 'relative' }}>
      <colgroup>
        {/* 공급받는자(왼쪽): 상호를 auto 로 최대 확보(거래처명이 길다) */}
        <col style={{ width: 20 }} />
        <col style={{ width: 66 }} /><col /><col style={{ width: 44 }} /><col style={{ width: 54 }} />
        {/* 공급자(오른쪽): 자사(그린푸드)라 짧고 고정 — 상호·값칸 고정폭 */}
        <col style={{ width: 20 }} />
        <col style={{ width: 66 }} /><col style={{ width: 78 }} /><col style={{ width: 44 }} /><col style={{ width: 88 }} />
      </colgroup>
      <tbody>
        <tr>
          <VLabel text="공급받는자" accent={accent} />
          {L('등 록 번 호')}{V(receiver.biz_no, 3)}
          <VLabel text="공급자" accent={accent} />
          {L('등 록 번 호')}
          <td colSpan={3} style={{ border: `1px solid ${accent}`, padding: '1px 5px', position: 'relative' }}>
            <FitText text={supplier.biz_no || ''} base={12} />
            {seal && (
              // 직인: 등록번호 셀에 앵커하되 아래로 내려(성명/주소 영역) 상단
              // '당거래액' 을 가리지 않게. top 은 셀 상단 기준 고정 px.
              <img src={seal} alt="인" style={{
                position: 'absolute', right: 2, top: 34, transform: 'translateY(-50%)',
                height: 56, opacity: 0.92, mixBlendMode: 'multiply', pointerEvents: 'none',
              }} />
            )}
          </td>
        </tr>
        <tr>
          {L('상 호')}{V(receiver.name)}{L('성 명')}{V(receiver.rep_name)}
          {L('상 호')}{V(supplier.name)}{L('성 명')}{V(supplier.rep_name)}
        </tr>
        <tr>
          {L('주 소')}{V(receiver.address, 3)}
          {L('주 소')}{V(supplier.address, 3)}
        </tr>
        <tr>
          {L('업 태')}{V(receiver.biz_kind)}{L('종 목')}{V(receiver.biz_item)}
          {L('업 태')}{V(supplier.biz_kind)}{L('종 목')}{V(supplier.biz_item)}
        </tr>
      </tbody>
    </table>
  )
}

/* ─────────────────────────── 큰장 (파랑)─────────────────────────── */
// 한 장당 고정 20줄. 품목이 20줄을 넘으면 다음 장으로 이어짐(합계는 전표 전체 기준 반복).
const BIG_ROWS_PER_PAGE = 20

export function BigSheet({ data, setting, hidePrice }: { data: InvoiceData; setting: InvoiceSetting; hidePrice?: boolean }) {
  const lines = data.lines
  const chunks: InvoiceData['lines'][] = []
  for (let i = 0; i < Math.max(lines.length, 1); i += BIG_ROWS_PER_PAGE) {
    chunks.push(lines.slice(i, i + BIG_ROWS_PER_PAGE))
  }
  return (
    <>
      {chunks.map((chunk, pi) => (
        <BigPage key={pi} data={data} lines={chunk} startNo={pi * BIG_ROWS_PER_PAGE} setting={setting} hidePrice={hidePrice} />
      ))}
    </>
  )
}

function BigPage({ data, lines, startNo, setting, hidePrice }: {
  data: InvoiceData; lines: InvoiceData['lines']; startNo: number; setting: InvoiceSetting; hidePrice?: boolean
}) {
  const a = BLUE
  const bf = bigFlags(data.company.invoice_style)
  const empty = Math.max(0, BIG_ROWS_PER_PAGE - lines.length)
  // 거래처 명세서 설정(setting) + '단가 없이 인쇄'(hidePrice) 를 함께 반영.
  const showUnit  = !hidePrice && setting.show_unit_price      // 7.단가
  const showAmt   = !hidePrice && setting.show_amount          // 8.금액(공급가액·세액)
  const showTot   = !hidePrice && setting.show_total_amount    // 9.합계금액
  const showToday = !hidePrice && setting.show_today_total     // 16.당일거래총액
  const priceCell = (v: string | number | null | undefined) => (showUnit ? fmtInt(v) : '')
  const amtCell   = (v: string | number | null | undefined) => (showAmt ? fmtInt(v) : '')
  // 소계/합계는 전표 전체(그랜드 토탈) 기준 — 여러 장으로 나뉘어도 동일 표시.
  const qtyTotal = data.lines.reduce((s, l) => s + Number(l.qty || 0), 0)
  const ROW_H = 28
  const nCols = bf.showCode ? 9 : 7           // 빈 행 셀 수
  const subColSpan = bf.showCode ? 5 : 3      // 소계 라벨 병합 폭
  const th = (t: string, w?: number) => (
    <th style={{ border: `1px solid ${a}`, color: a, padding: '3px 3px', fontWeight: 700, width: w }}>{t}</th>
  )
  const td = (t: React.ReactNode, align: 'left' | 'right' | 'center' = 'left') => (
    <td style={{ border: `1px solid ${a}`, padding: '1px 5px', textAlign: align, height: ROW_H }}>{t}</td>
  )
  return (
    <div className="invoice-sheet" style={sheetStyle}>
      <h1 style={{ textAlign: 'center', color: a, fontSize: 30, fontWeight: 700, letterSpacing: '0.35em', margin: '4px 0 14px' }}>거 래 명 세 서</h1>
      <div style={{ fontSize: 13, marginBottom: 3 }}><b style={{ color: a }}>납품년월일 :</b>&nbsp;&nbsp;{fmtDate(data.date)}</div>
      <div style={{ fontSize: 13, marginBottom: 6, color: a }}>증빙번호 : <span style={{ color: '#000' }}>{data.orders.join(', ')}</span></div>

      <PartyTable accent={a} receiver={data.partner} supplier={data.company} />

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed', marginTop: -1 }}>
        <colgroup>
          {bf.showCode && <><col style={{ width: 26 }} /><col style={{ width: 60 }} /></>}
          <col /><col style={{ width: 70 }} />
          <col style={{ width: 40 }} /><col style={{ width: 56 }} /><col style={{ width: 70 }} />
          <col style={{ width: 86 }} /><col style={{ width: 74 }} />
        </colgroup>
        <thead>
          <tr>{bf.showCode && <>{th('No.')}{th('코 드')}</>}{th('품 명')}{th('규 격')}{th('단 위')}{th('수 량')}{th('단 가')}{th('공급가액')}{th('세 액')}</tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              {bf.showCode && <>{td(startNo + i + 1, 'center')}{td(l.item_code, 'left')}</>}
              {td(l.item_name, 'left')}{td(l.spec, 'center')}
              {td(l.unit, 'center')}{td(fmtQty(l.qty), 'right')}{td(priceCell(l.unit_price), 'right')}
              {td(amtCell(l.supply_amount), 'right')}{td(amtCell(l.vat_amount), 'right')}
            </tr>
          ))}
          {Array.from({ length: empty }).map((_, i) => (
            <tr key={`e${i}`}>{blanks(nCols, a, ROW_H)}</tr>
          ))}
          <tr style={{ fontWeight: 700 }}>
            <td colSpan={subColSpan} style={{ border: `1px solid ${a}`, color: a, textAlign: 'center', padding: '2px' }}>소 계</td>
            {td(fmtQty(qtyTotal), 'right')}{td(' ')}{td(amtCell(data.subtotal), 'right')}{td(amtCell(data.vat_total), 'right')}
          </tr>
        </tbody>
      </table>

      {showTot && (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed', marginTop: -1 }}>
        <colgroup><col style={{ width: 26 }} /><col /><col style={{ width: 140 }} /><col style={{ width: 160 }} /></colgroup>
        <tbody>
          <tr>
            <td rowSpan={3} style={{ border: `1px solid ${a}`, color: a, textAlign: 'center', fontWeight: 700, width: 26 }}>비<br />고</td>
            <td rowSpan={3} style={{ border: `1px solid ${a}`, padding: '2px 6px', fontSize: 12, verticalAlign: 'top', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{data.note || ' '}</td>
            <td style={{ border: `1px solid ${a}`, color: a, textAlign: 'center', fontWeight: 700, padding: '3px' }}>공 급 가 액 합 계</td>
            <td style={{ border: `1px solid ${a}`, textAlign: 'right', padding: '3px 6px' }}>{fmtInt(data.subtotal)}</td>
          </tr>
          <tr>
            <td style={{ border: `1px solid ${a}`, color: a, textAlign: 'center', fontWeight: 700, padding: '3px' }}>부 가 가 치 세</td>
            <td style={{ border: `1px solid ${a}`, textAlign: 'right', padding: '3px 6px' }}>{fmtInt(data.vat_total)}</td>
          </tr>
          <tr>
            <td style={{ border: `1px solid ${a}`, color: a, textAlign: 'center', fontWeight: 700, padding: '3px' }}>총 계</td>
            <td style={{ border: `1px solid ${a}`, textAlign: 'right', fontWeight: 700, padding: '3px 6px' }}>{fmtInt(data.today_total)}</td>
          </tr>
        </tbody>
      </table>
      )}

      {setting.show_receivable && (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed', marginTop: -1 }}>
        <thead>
          <tr style={{ color: a }}>{['전미수액', '당일거래총액', '입금액', '미수액', '인수자'].map((h) => (
            <th key={h} style={{ border: `1px solid ${a}`, fontWeight: 700, padding: '2px' }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ border: `1px solid ${a}`, textAlign: 'right', padding: '3px 6px' }}>{fmtInt(data.outstanding_before)}</td>
            <td style={{ border: `1px solid ${a}`, textAlign: 'right', padding: '3px 6px', fontWeight: 700 }}>{fmtInt(data.today_total)}</td>
            <td style={{ border: `1px solid ${a}`, textAlign: 'right', padding: '3px 6px' }}>{fmtInt(data.paid_today)}</td>
            <td style={{ border: `1px solid ${a}`, textAlign: 'right', padding: '3px 6px', fontWeight: 700 }}>{fmtInt(data.outstanding_after)}</td>
            <td style={{ border: `1px solid ${a}`, textAlign: 'center', color: '#94a3b8' }}>서명</td>
          </tr>
        </tbody>
      </table>
      )}

      {bf.showTodayTotal && showToday && !setting.show_receivable && (
        <div style={{ textAlign: 'center', color: a, fontSize: 13, marginTop: 8 }}>
          당일거래총액&nbsp;&nbsp;&nbsp;&nbsp;{fmtInt(data.today_total)}
        </div>
      )}
    </div>
  )
}

/* ─────────────────────────── 작은장 (빨강, 1장 2건) ─────────────────────────── */
function SmallCopy({ data, lines, note, rowsPerHalf, accent, hidePrice, setting, startNo = 0 }: {
  data: InvoiceData; lines: InvoiceData['lines']; note: string; rowsPerHalf: number; accent?: string; hidePrice?: boolean; setting: InvoiceSetting; startNo?: number
}) {
  const a = accent ?? RED   // 공급자보관용=빨강, 공급받는자보관용=파랑
  // 거래처 명세서 설정(setting) + '단가 없이 인쇄'(hidePrice) 를 함께 반영.
  const showUnit  = !hidePrice && setting.show_unit_price      // 7.단가
  const showAmt   = !hidePrice && setting.show_amount          // 8.금액
  const showTot   = !hidePrice && setting.show_total_amount    // 9.합계금액
  const showToday = !hidePrice && setting.show_today_total     // 16.당일거래총액
  const priceCell = (v: string | number | null | undefined) => (showUnit ? fmtInt(v) : '')
  const amtCell   = (v: string | number | null | undefined) => (showAmt ? fmtInt(v) : '')
  const totCell   = (v: string | number | null | undefined) => (showTot ? fmtInt(v) : '')
  const todayCell = (v: string | number | null | undefined) => (showToday ? fmtInt(v) : '')
  const sf = smallFlags(data.company.invoice_style)
  const smallCols = 6 + (sf.splitAmount ? 2 : 1) + (sf.showItemMemo ? 1 : 0)
  const empty = Math.max(0, rowsPerHalf - lines.length)
  // 수량계/총계 등은 전표 전체 기준(그랜드 토탈) — 페이지가 나뉘어도 동일 표시.
  const qtyTotal = data.lines.reduce((s, l) => s + Number(l.qty || 0), 0)
  const th = (t: string) => <th style={{ border: `1px solid ${a}`, color: a, padding: '1px 2px', fontWeight: 700 }}>{t}</th>
  const td = (t: React.ReactNode, align: 'left' | 'right' | 'center' = 'left') => (
    <td style={{ border: `1px solid ${a}`, padding: '1px 4px', textAlign: align, height: 23 }}>{t}</td>
  )
  // 품명·규격: 칸을 넘치면 줄바꿈(행 높이 증가) 대신 폰트만 자동 축소해 한 줄 유지.
  const tdFit = (t: string, align: 'left' | 'right' | 'center' = 'left') => (
    <td style={{ border: `1px solid ${a}`, padding: '1px 4px', textAlign: align, height: 23 }}>
      <FitText text={t || ''} base={12} min={7} />
    </td>
  )
  const cell = (label: string, value: React.ReactNode) => (
    <>
      <td style={{ border: `1px solid ${a}`, color: a, fontWeight: 700, textAlign: 'center', padding: '1px 3px' }}>{label}</td>
      <td style={{ border: `1px solid ${a}`, textAlign: 'right', padding: '1px 4px' }}>{value}</td>
    </>
  )
  return (
    <div>
      <h1 style={{ textAlign: 'center', color: a, fontSize: 24, fontWeight: 700, letterSpacing: '0.3em', margin: '2px 0 0', textDecoration: 'underline' }}>거 래 명 세 서</h1>
      <div style={{ textAlign: 'center', color: a, fontSize: 11, marginBottom: 3 }}>( {note} )</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 2 }}>
        <span><b style={{ color: a }}>납품년월일 :</b> {fmtDate(data.date)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 3 }}>
        <span style={{ color: a }}>증빙번호 : <span style={{ color: '#000' }}>{data.orders.join(', ')}</span></span>
        {showTot && <span style={{ color: a }}>당거래액 : <span style={{ color: '#000' }}>{fmtInt(data.today_total)}</span></span>}
      </div>

      <PartyTable accent={a} receiver={data.partner} supplier={data.company} />

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed', marginTop: -1 }}>
        <colgroup>
          {/* 품명(auto) 여백 축소: 코드/규격/수량/단가/금액을 넉넉히 잡아 숫자 블록을 왼쪽으로 당김.
             표는 100% 폭 유지(위/아래 박스와 테두리 정렬) — 남는 폭을 품명 대신 값 열들이 흡수. */}
          <col style={{ width: sf.showSeq ? 34 : 62 }} /><col /><col style={{ width: 84 }} /><col style={{ width: 40 }} />
          <col style={{ width: 60 }} /><col style={{ width: 78 }} />
          {sf.splitAmount
            ? <><col style={{ width: 72 }} /><col style={{ width: 64 }} /></>
            : <col style={{ width: 98 }} />}
          {sf.showItemMemo && <col style={{ width: 70 }} />}
        </colgroup>
        <thead>
          <tr>
            {th(sf.showSeq ? '순번' : '코 드')}{th('품 명')}{th('규 격')}{th('단위')}{th('수 량')}{th('단 가')}
            {sf.splitAmount ? <>{th('공급가액')}{th('세 액')}</> : th('금 액')}
            {sf.showItemMemo && th('비 고')}
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              {sf.showSeq ? td(startNo + i + 1, 'center') : td(l.item_code)}
              {tdFit(l.item_name)}{tdFit(l.spec, 'center')}{td(l.unit, 'center')}
              {td(fmtQty(l.qty), 'right')}{td(priceCell(l.unit_price), 'right')}
              {sf.splitAmount
                ? <>{td(amtCell(l.supply_amount), 'right')}{td(amtCell(l.vat_amount), 'right')}</>
                : td(amtCell(l.total), 'right')}
              {sf.showItemMemo && td(l.note)}
            </tr>
          ))}
          {Array.from({ length: empty }).map((_, i) => (
            <tr key={`e${i}`}>{blanks(smallCols, a, 23)}</tr>
          ))}
          {sf.footerSum && (
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={4} style={{ border: `1px solid ${a}`, color: a, textAlign: 'center', padding: '1px' }}>합 계</td>
              {td(fmtQty(qtyTotal), 'right')}{td(' ')}
              {td(totCell(data.subtotal), 'right')}{td(totCell(data.vat_total), 'right')}
              {sf.showItemMemo && td(' ')}
            </tr>
          )}
        </tbody>
      </table>

      {!sf.footerSum && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5, tableLayout: 'fixed', marginTop: -1 }}>
          <tbody>
            <tr style={{ fontWeight: 700 }}>
              {cell('수량계', fmtQty(qtyTotal))}{cell('총계', totCell(data.today_total))}
              {cell('부가세계', totCell(data.vat_total))}{cell('공급가액계', totCell(data.subtotal))}
            </tr>
          </tbody>
        </table>
      )}

      <div style={{ fontSize: 11, color: a, margin: '2px 0' }}>
        담당자 : &nbsp;&nbsp;&nbsp;&nbsp; TEL. : {data.company.tel} &nbsp;&nbsp;&nbsp;&nbsp; FAX. : {data.company.fax}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, tableLayout: 'fixed' }}>
        <thead>
          <tr style={{ color: a }}>{(setting.show_receivable
            ? ['비 고', '전미수액', '당일거래총액', '입금액', '미수액', '인수자']
            : ['비 고', '당일거래총액', '입금액', '인수자']).map((h) => (
            <th key={h} style={{ border: `1px solid ${a}`, fontWeight: 700, padding: '1px' }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ border: `1px solid ${a}`, padding: '1px 4px', fontSize: 9.5, verticalAlign: 'top', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{data.note || ' '}</td>
            {setting.show_receivable && (
              <td style={{ border: `1px solid ${a}`, textAlign: 'right', padding: '1px 4px' }}>{totCell(data.outstanding_before)}</td>
            )}
            <td style={{ border: `1px solid ${a}`, textAlign: 'right', padding: '1px 4px', fontWeight: 700 }}>{todayCell(data.today_total)}</td>
            <td style={{ border: `1px solid ${a}`, textAlign: 'right', padding: '1px 4px' }}>{totCell(data.paid_today)}</td>
            {setting.show_receivable && (
              <td style={{ border: `1px solid ${a}`, textAlign: 'right', padding: '1px 4px', fontWeight: 700 }}>{totCell(data.outstanding_after)}</td>
            )}
            <td style={{ border: `1px solid ${a}`, textAlign: 'center', color: '#94a3b8' }}>서명</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

// 작은장: A4 한 장을 위·아래 정확히 반으로 — 위=공급자보관용, 아래=공급받는자보관용
// (같은 품목을 양쪽에 표시, 가운데 절취선에서 잘라 한 부씩 나눠 가짐).
// 품목이 반 페이지(ROWS_PER_HALF)를 넘으면 다음 A4로 이어짐(각 장도 가운데 절단).
const ROWS_PER_HALF = 10

export function SmallSheet({ data, setting, hidePrice }: { data: InvoiceData; setting: InvoiceSetting; hidePrice?: boolean }) {
  const lines = data.lines
  const chunks: InvoiceData['lines'][] = []
  for (let i = 0; i < Math.max(lines.length, 1); i += ROWS_PER_HALF) {
    chunks.push(lines.slice(i, i + ROWS_PER_HALF))
  }
  return (
    <>
      {chunks.map((chunk, pi) => (
        <div key={pi} className="invoice-sheet" style={smallPageStyle}>
          <div style={smallCopyStyle}>
            <SmallCopy data={data} lines={chunk} note="공급자보관용" rowsPerHalf={ROWS_PER_HALF} accent={RED} hidePrice={hidePrice} setting={setting} startNo={pi * ROWS_PER_HALF} />
          </div>
          <div style={cutLineStyle}>
            <span style={cutLabelStyle}>✂ 절취선</span>
          </div>
          <div style={smallCopyStyle}>
            <SmallCopy data={data} lines={chunk} note="공급받는자보관용" rowsPerHalf={ROWS_PER_HALF} accent={BLUE} hidePrice={hidePrice} setting={setting} startNo={pi * ROWS_PER_HALF} />
          </div>
        </div>
      ))}
    </>
  )
}

const sheetStyle: React.CSSProperties = {
  background: '#fff',
  width: '190mm',
  minHeight: '277mm',
  margin: '0 auto',
  // 상하는 @page margin(6mm)이 프린터 비인쇄영역 여백을 담당 → 시트 자체 상하 패딩은
  // 2mm 로 줄여 순 높이 손실 최소화(밀도 높은 명세서가 2페이지로 넘어가는 것 방지).
  padding: '2mm 8mm',
  boxSizing: 'border-box',
  fontFamily: '"Malgun Gothic", "Apple SD Gothic Neo", sans-serif',
  pageBreakAfter: 'always',
  WebkitPrintColorAdjust: 'exact',
  printColorAdjust: 'exact',
}

// 작은장 A4 한 장(≈296mm) = 상단 147.5mm + 절취선 1mm + 하단 147.5mm.
const smallPageStyle: React.CSSProperties = {
  background: '#fff',
  width: '190mm',
  margin: '0 auto',
  fontFamily: '"Malgun Gothic", "Apple SD Gothic Neo", sans-serif',
  pageBreakAfter: 'always',
  WebkitPrintColorAdjust: 'exact',
  printColorAdjust: 'exact',
}
const smallCopyStyle: React.CSSProperties = {
  height: '147.5mm',
  overflow: 'hidden',
  boxSizing: 'border-box',
  padding: '4mm 8mm 0',
}
const cutLineStyle: React.CSSProperties = {
  height: '1mm',
  borderTop: '1px dashed #94a3b8',
  position: 'relative',
}
const cutLabelStyle: React.CSSProperties = {
  position: 'absolute',
  right: '8mm',
  top: '-7px',
  background: '#fff',
  padding: '0 4px',
  fontSize: 9,
  color: '#94a3b8',
}
